import { faCheckSquare, faEdit, faTrash, faWindowClose } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment/moment";
import React, { Fragment, useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import ReactTable from "react-table";
import * as api from "../../api/IrsClient";
import { ERROR_PREFIX_REGEX, EXCHANGE_RATE_TYPE } from "../../constant";
import ExchangeRateModal from "./ExchangeRateModal";

function ExchangeRateTable({
    userGroup,
    exchangeRatePageable,
    onPageRequestChange,
    /**
     * Callback that is called when an update or delete of an exchange rate is successful.
     */
    onExchangeRateUpdateSuccess,
    onSortedChange,
    isLoading,
    setInfoModal,
    setErrorModal,
    setDismissModal
}) {
    const [isUpdateRateLoading, setIsUpldateRateLoading] = useState(false);
    const [selectedExchangeRate, setSelectedExchangeRate] = useState(null);
    const [isShownModalEditExchangeRate, setIsShownModalEditExchangeRate] = useState(false);
    const [pageable, setPageable] = useState({ page: 0, numberOfPage: 1, pageSize: 10 });
    const isAdminRole = userGroup?.includes("/services/irs/irs-administrator");
    const isFxRole = userGroup?.includes("/services/irs/irs-exchange-rate");

    useEffect(() => {
        // calculate page info
        if (exchangeRatePageable) {
            setPageable({
                page: exchangeRatePageable.page - 1,
                pageSize: exchangeRatePageable.total_per_page,
                numberOfPage: Math.ceil(exchangeRatePageable.total / exchangeRatePageable.total_per_page)
            });
        }
    }, [exchangeRatePageable]);

    function handleOnPageChange(page) {
        onPageRequestChange(page + 1, pageable.pageSize)
    }

    function handleOnPageSizeChange(pageSize) {
        onPageRequestChange(pageable.page + 1, pageSize);
    }

    /**
     * Handles the delete button click for a specific exchange rate.
     *
     * @param {Object} exchangeRate - The exchange rate object to be deleted.
     */
    function handleOnClickDelete(exchangeRate) {
        setInfoModal({
            title: `Do you want to delete "${exchangeRate.label}"?`,
            description: "This operation cannot be undone",
            textOk: "Yes",
            textCancel: "No",
            onOk: () => {
                deleteExchangeRate(exchangeRate.exchange_rate_id);
                setInfoModal(null);
            }
        });
    }

    async function deleteExchangeRate(id) {
        try {
            setIsUpldateRateLoading(true);
            const { data: { status } } = await api.deleteExchangeRate(id);
            if (status.code !== "0") {
                setErrorModal({
                    title: "Customer rate delete failed!",
                    description: status.message
                });
            } else {
                onExchangeRateUpdateSuccess();
            }
        } catch (error) {
            setErrorModal({
                description: "Customer rate delete failed!"
            });
        } finally {
            setIsUpldateRateLoading(false);
        }
    }

    function handleOnEditExchangeRate(data) {
        setSelectedExchangeRate(data);
        setIsShownModalEditExchangeRate(true);
    }

    /**
     * Handles the update of an exchange rate.
     *
     * @param {Object} data - The updated data for the exchange rate.
     */
    async function handleOnOkEditExchangeRate(data) {
        try {
            setIsUpldateRateLoading(true);
            const { data: { status } } = await api.createUpdateExchangeRateV4(data);
            if (status.code !== "0") {
                setErrorModal({
                    title: "Customer rate update failed!",
                    description: status.message.replace(ERROR_PREFIX_REGEX, "")
                });
            } else {
                setInfoModal({
                    title: "Update the customer rate successfully",
                    textCancel: "OK"
                });
                setIsShownModalEditExchangeRate(false);
                onExchangeRateUpdateSuccess();
            }
        } catch (error) {
            setErrorModal({
                description: error.response?.data?.status.message.replace(ERROR_PREFIX_REGEX, "")
            });
        } finally {
            setIsUpldateRateLoading(false);
        }
    }

    function handleOnCancelEditModal() {
        setDismissModal({
            onCancel: () => {
                setIsShownModalEditExchangeRate(false);
                setSelectedExchangeRate(null);
                setDismissModal(null);
            }
        })
    }

    function showEnable(value) {
        if (value) {
            return <FontAwesomeIcon icon={faCheckSquare} size="2x" color={"green"} />
        } else {
            return <FontAwesomeIcon icon={faWindowClose} size="2x" color={"red"} />
        }
    }

    function showType(value) {
        if (value === EXCHANGE_RATE_TYPE.GENERIC) {
            return <span className="badge badge-primary p-2">{EXCHANGE_RATE_TYPE.GENERIC}</span>
        } else if (value === EXCHANGE_RATE_TYPE.NEW_CUSTOMER) {
            return <span className="badge badge-info p-2">{EXCHANGE_RATE_TYPE.NEW_CUSTOMER}</span>
        } else if (value === EXCHANGE_RATE_TYPE.SPECIAL_CUSTOMER) {
            return <span className="badge badge-success p-2">{EXCHANGE_RATE_TYPE.SPECIAL_CUSTOMER}</span>
        } else if (value === EXCHANGE_RATE_TYPE.SPECIAL_TRANSACTION) {
            return <span className="badge alert-success p-2">{EXCHANGE_RATE_TYPE.SPECIAL_TRANSACTION}</span>
        }
    }

    function prepareColumns() {
        return [
            {
                Header: "Action",
                accessor: "",
                Cell: (props) => {
                    return (
                        <Fragment>
                            {(props.original.type !== EXCHANGE_RATE_TYPE.GENERIC || isFxRole) && (
                                <button
                                    className="btn btn-icon btn-warning"
                                    data-testid={`edit-exchange-rate-button`}
                                    onClick={() => handleOnEditExchangeRate(props.original)}
                                >
                                    <FontAwesomeIcon icon={faEdit} />
                                </button>
                            )}
                            {(props.original.type !== EXCHANGE_RATE_TYPE.GENERIC && isAdminRole) && (
                                <button
                                    className="btn btn-icon btn-secondary ml-1"
                                    data-testid={`delete-exchange-rate-button`}
                                    onClick={() => handleOnClickDelete(props.original)}
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                </button>
                            )}
                        </Fragment>

                    );
                }
            },
            {
                Header: "Country",
                accessor: "country_name",
            },
            {
                Header: "Currency",
                accessor: "currency_unit",
            },
            {
                Header: "Name",
                accessor: "label",

            },
            {
                Header: "Type",
                accessor: "type",
                Cell: ({ value }) => {
                    return showType(value);
                }

            },
            {
                Header: "TMN Rate",
                accessor: "rate",
            },
            {
                Header: "Minimum Transaction Amount (THB)",
                accessor: "min_trans_amount",
                sortable: true
            },
            {
                Header: "Is Enabled?",
                accessor: "is_disable",
                Cell: ({ value }) => {
                    return showEnable(!value);
                }
            },
            {
                Header: "Last Update",
                accessor: "updated_date",
                Cell: ({ value }) => moment(value).format("DD-MM-YYYY / HH:mm:ss"),
            },
            {
                Header: "Last Update By",
                accessor: "updated_by",
            },
        ];
    }

    return (
        <div className="p-3">
            <ReactTable
                data={exchangeRatePageable.exchange_rates}
                showPaginationTop={true}
                columns={prepareColumns()}
                manual
                pageSizeOptions={[10, 25, 50, 100]}
                page={pageable.page}
                pages={pageable.numberOfPage}
                pageSize={pageable.pageSize}
                onPageChange={handleOnPageChange}
                onPageSizeChange={handleOnPageSizeChange}
                onSortedChange={onSortedChange}
                loading={isLoading}
                className="-highlight report"
                resizable={true}
                sortable={false}
                minRows={10}
            />
            <ExchangeRateModal
                header="Edit Customer Rate"
                data={selectedExchangeRate}
                loading={isUpdateRateLoading}
                isOpen={isShownModalEditExchangeRate}
                onRequestClose={handleOnCancelEditModal}
                onOk={handleOnOkEditExchangeRate}
                onCancel={handleOnCancelEditModal}
                textOk="Close"
                width={600}
                userGroup={userGroup}
                mode="EDIT"
            />
        </div>

    );

}

export default withTranslation()(ExchangeRateTable);